import React, { useState, useRef } from "react"

import { Switch, Route, useHistory } from "react-router-dom"

import PrivateRoutes from "@/routes/private"
import PublicRoutes from "@/routes/public"
import DevelopmentRoutes from "@/routes/development"

import { Loading } from "@/components"

import useDidMount from "@/hooks/useDidMount"
import useSocket from "@/hooks/useSocket"
import useAuthReturnUrl from "@/hooks/useAuthReturnUrl"
import { useGlobalStateStore } from "@/store/GlobalState"
import { usePermissionStateStore } from "@/store/PermissionState"

import ErrorHandlerService from "@/services/ErrorHandler"
import UserService from "@/services/User"
import AuthService from "@/services/Auth"
import SocketService from "@/services/Socket"
import ChannelService from "@/services/Channel"
import StorageService from "@/services/Storage"
import TeamService from "@/services/Team"
import ApiService from "@/services/Api"
import GoogleService from "@/services/Google"
import DateService from "@/services/Date"
import ObservabilityService from "@/services/Observability"
import AnalyticsService from "@/services/Analytics"

import { isDevEnv } from "@/utils/environment"

import { STORAGE_KEY } from "@/components/GlobalErrorBoundary/FallbackScreen"

import { ErrorType } from "@/hooks/useValidation"
import { redirectToInstanceChangedPage } from "@/utils/url"

const PrivateComponent = () => {
	const [loading, setLoading] = useState(true)
	const [criticalError, setCriticalError] = useState<Error | null>(null)

	const globalStateStore = useGlobalStateStore()
	const permissionStateStore = usePermissionStateStore()

	const socket = useSocket()
	const { redirectToSignInWithReturnUrl } = useAuthReturnUrl()

	/**
	 * We need to put instanceId inside ref in other to call
	 * its actual value inside listeners.
	 */
	const instanceIdRef = useRef<number>()

	const setupChannelListeners = () => {
		socket.onChannelChanged(updatedChannel => {
			globalStateStore.setChannelsData(lastState => {
				const channelExists = lastState.some(channel => channel.id === updatedChannel.id)

				let updatedState = [...lastState]

				/**
				 * We need to validate since a channel can not exist in state
				 * in case it was a created channels, since sometimes channels
				 * are updated and other times created.
				 */
				if (channelExists) {
					updatedState = lastState.map(channel => {
						if (channel.id === updatedChannel.id) {
							return {
								...channel,
								...updatedChannel
							}
						}

						return channel
					})
				} else {
					updatedState.push(updatedChannel)
				}

				return updatedState
			})
		})

		socket.onSocketReady(async () => {
			const instanceId = instanceIdRef.current

			if (instanceId) {
				const refreshedInboxChannels = await ChannelService.setupChannels(instanceId)

				globalStateStore.setChannelsData(oldInboxChannels => {
					const refreshedInboxChannelsWithOldSettings = (refreshedInboxChannels || []).map(refreshedInboxChannel => {
						const oldInboxChannel = oldInboxChannels.find(({ id }) => id === refreshedInboxChannel.id)

						return {
							...refreshedInboxChannel,
							/**
							 * WARNING:
							 * - Since 'inboxChannelUserSettings' is injected into the channel's global state,
							 * we must ensure it is not overridden, as this can cause bugs such as the message
							 * text starting with 'undefined' instead of the attendant's signature.
							 *
							 * TODO: move 'inboxChannelUserSettings' to a separate state to avoid potential conflicts.
							 */
							settings: oldInboxChannel?.settings
						}
					})

					return refreshedInboxChannelsWithOldSettings
				})
			}
		})

		socket.onRemoveSession(async payload => {
			if (payload.authToken === AuthService.authToken) {
				await AuthService.logout("/signin")
			}
		})
	}

	const getInitialData = async () => {
		const isLoggedIn = await AuthService.isLoggedIn()

		if (!isLoggedIn) {
			redirectToSignInWithReturnUrl()
			return
		}

		try {
			const user = await UserService.getInfo()

			if (!user) {
				setCriticalError(new Error("UserService.getInfo() returned null with a valid session"))
				return
			}

			const {
				userData,
				userInInstanceData,
				subscriptionData,
				merlinData,
				botVersions,
				authenticationsData,
				instanceData,
				cannyToken
			} = user

			ObservabilityService.setTracingUserData({
				id: userData.id,
				name: userData.name,
				email: userData.email,
				instanceId: userInInstanceData.instance_id
			})

			AnalyticsService.setUserData({
				id: userData.id,
				instanceId: userInInstanceData.instance_id,
				name: userData.name,
				email: userData.email
			})

			if (userData.extra_data?.admin_id) {
				StorageService.set(StorageService.reservedKeys.INBOX_ADMIN_ID, userData.extra_data.admin_id)
			}

			const instanceId = userInInstanceData.instance_id
			instanceIdRef.current = instanceId

			const actualInstanceId = StorageService.get(StorageService.reservedKeys.INBOX_INSTANCE_ID)

			StorageService.set(StorageService.reservedKeys.INBOX_INSTANCE_ID, instanceId)

			if (actualInstanceId !== instanceId) {
				redirectToInstanceChangedPage()
			}

			const [
				channels,
				teams,
				gtmVariables
			] = await Promise.all([
				ChannelService.setupChannels(instanceId),
				TeamService.getTeams(),
				GoogleService.getGTMVariables(),
				SocketService.setup(instanceId, user.authToken),
				DateService.loadServerTime()
			])

			globalStateStore.setUserData({
				id: userData.id,
				name: userData.name,
				email: userData.email,
				phone_number: userData.phone_number,
				extra_data: userData.extra_data,
				user_teams: userInInstanceData.user_teams,
				valid_teams: userInInstanceData.user_teams.map(team => team.id),
				is_instance_owner: userInInstanceData.is_instance_owner,
				botVersions,
				created_at: userData.created_at,
				user_instances: userData.user_instances,
				user_instances_is_owner: userData.user_instances_is_owner,
				authenticationsData: authenticationsData,
				cannyToken
			})

			permissionStateStore.setPermissionData({
				permissions: userData.permissions
			})

			globalStateStore.setInstanceData({
				instance_id: userInInstanceData.instance_id,
				responsible_phone_number: instanceData.responsible_phone_number,
				nickname: instanceData.nickname,
				instance_created_at: userInInstanceData.instance_created_at,
				user_in_instance_id: userInInstanceData.id,
				subscriptionData,
				teams,
				merlinData,
				current_channel_type: instanceData?.current_channel_type,
				is_terms_of_service_accepted: instanceData?.is_terms_of_service_accepted,
				roles: instanceData?.roles || []
			})

			globalStateStore.setChannelsData(channels || [])

			sessionStorage.removeItem(STORAGE_KEY)

			setupChannelListeners()

			GoogleService.addDataLayerVariables("application_loaded", gtmVariables)

			GoogleService.addDataLayerVariables("page_view", {
				is_admin: Boolean(userData?.extra_data?.is_admin_mode),
				is_tester: Boolean(userData?.extra_data?.is_tester),
				is_first_access: Boolean(userData?.extra_data?.is_first_access),
				is_trial: Boolean(subscriptionData?.isTrial)
			})

			if (
				userData?.extra_data?.is_first_access &&
				!userData?.extra_data?.is_admin_mode
			) {
				window.onunload = await ApiService.put("/user/revoke/extra-data/first-access")
			}

			window.Canny("identify", {
				appID: "6686b336be9f9cb0165d0796",
				user: {
					email: userData.email,
					id: String(userData.id),
					name: userData.name
				}
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	useDidMount(() => {
		getInitialData()
	})

	if (criticalError) {
		throw criticalError
	}

	return (
		<Loading loading={loading}>
			<PrivateRoutes />
		</Loading>
	)
}

const Routes = () => {
	const history = useHistory()

	history.listen(({ pathname }) => {
		GoogleService.triggerGoogleAnalyticsPageView(pathname)
	})

	useDidMount(() => {
		GoogleService.initGoogleTagManager()
	})

	return (
		<Switch>
			{isDevEnv && DevelopmentRoutes}

			{PublicRoutes}

			<Route path="/" component={PrivateComponent} />
			<Route path="*" component={() => <h1>Page Not Found</h1>} />
		</Switch>
	)
}

export default Routes
